import Vue from "vue";
import {
    normalizeWidgetCarouselForm,
    getDefaultWidgetCarouselProduct,
    getDefaultWidgetCarouselProductItem
} from "../utils/widgetCarouselFactory";

function findProductIndexByLocalId(products, localId) {
    return products.findIndex((product) => product.local_id === localId);
}

function findProductItemIndexByLocalId(items, localId) {
    return items.findIndex((item) => item.local_id === localId);
}

function interpolateTemplate(template, product) {
    return String(template || "").replace(/\{\{(.*?)\}\}/g, function (_, rawKey) {
        const key = String(rawKey || "").trim();
        return resolveProductVariable(product, key);
    });
}

function resolveProductVariable(product, variableKey) {
    const safeProduct = product || {};
    const sourceType = safeProduct.source_type || "benefits";

    const benefitVariables = {
        product_name: safeProduct.product_name || "",
        product_name_en: safeProduct.product_name_en || "",
        price: safeProduct.price || "",
        discount_price: safeProduct.discount_price || "",
        user_discount: safeProduct.user_discount || "",
        discount: safeProduct.discount || "",
        brand_name: safeProduct.brand_name || ""
    };

    const offerVariables = {
        name: safeProduct.product_name || "",
        brand_name: safeProduct.brand_name || "",
        value: safeProduct.value || ""
    };

    const variables = sourceType === "offers"
        ? offerVariables
        : benefitVariables;

    return variables[variableKey] || "";
}

function reinterpolateBadgeList(list, product) {
    if (!Array.isArray(list)) {
        return [];
    }

    return list.map((badge) => {
        const clonedBadge = JSON.parse(JSON.stringify(badge || {}));
        const badgeItems = Array.isArray(clonedBadge.items) ? clonedBadge.items : [];

        clonedBadge.items = badgeItems.map((badgeItem) => {
            const clonedBadgeItem = JSON.parse(JSON.stringify(badgeItem || {}));

            if (clonedBadgeItem.content_mode === "mixed") {
                const rawRo =
                    clonedBadgeItem.title && typeof clonedBadgeItem.title.ro === "string"
                        ? clonedBadgeItem.title.ro
                        : "";
                const rawEn =
                    clonedBadgeItem.title && typeof clonedBadgeItem.title.en === "string"
                        ? clonedBadgeItem.title.en
                        : "";

                clonedBadgeItem.title = {
                    ro: rawRo,
                    en: rawEn
                };

                clonedBadgeItem.preview_title = {
                    ro: interpolateTemplate(rawRo, product),
                    en: interpolateTemplate(rawEn, product)
                };
            }

            return clonedBadgeItem;
        });

        return clonedBadge;
    });
}

function reinterpolateProductItems(items, product) {
    if (!Array.isArray(items)) {
        return [];
    }

    return items.map((item) => {
        const clonedItem = JSON.parse(JSON.stringify(item || {}));
        const badges = clonedItem.badges || {};

        clonedItem.badges = {
            top_left: reinterpolateBadgeList(badges.top_left, product),
            top_right: reinterpolateBadgeList(badges.top_right, product),
            bottom_left: reinterpolateBadgeList(badges.bottom_left, product),
            bottom_right: reinterpolateBadgeList(badges.bottom_right, product)
        };

        return clonedItem;
    });
}

function isLockedProduct(products, localId) {
    const productIndex = findProductIndexByLocalId(products, localId);

    if (productIndex === -1) {
        return false;
    }

    return !products[productIndex].is_active;
}

const state = () => ({
    mode: "create",
    activeTab: "general",
    selectedProductId: null,
    form: normalizeWidgetCarouselForm({})
});

const getters = {
    form: (state) => state.form,
    mode: (state) => state.mode,
    activeTab: (state) => state.activeTab,
    selectedProductId: (state) => state.selectedProductId,
    products: (state) => state.form.products,
    isWidgetActive: (state) => !!state.form.is_active,
    isProductsTabDisabled: (state) => !state.form.is_active,
    isCreateMode: (state) => state.mode === "create",
    isUpdateMode: (state) => state.mode === "update"
};

const mutations = {
    SET_MODE(state, mode) {
        state.mode = mode;
    },

    SET_FORM(state, form) {
        state.form = form;
    },

    SET_ACTIVE_TAB(state, tab) {
        state.activeTab = tab;
    },

    SET_SELECTED_PRODUCT_ID(state, localId) {
        state.selectedProductId = localId;
    },

    SET_FIELD(state, { field, value }) {
        Vue.set(state.form, field, value);
    },

    SET_I18N_FIELD(state, { field, lang, value }) {
        if (!state.form[field]) {
            Vue.set(state.form, field, {});
        }

        Vue.set(state.form[field], lang, value);
    },

    SET_GLOBAL_BACKGROUND_IMAGE(state, image) {
        Vue.set(state.form, "global_background_image", image);
    },

    ADD_PRODUCT(state, product) {
        state.form.products.push(getDefaultWidgetCarouselProduct(product));
    },

    REMOVE_PRODUCT(state, localId) {
        const productIndex = findProductIndexByLocalId(state.form.products, localId);

        if (productIndex === -1) {
            return;
        }

        state.form.products.splice(productIndex, 1);

        if (state.selectedProductId === localId) {
            state.selectedProductId = null;
        }
    },

    DUPLICATE_PRODUCT(state, localId) {
        const productIndex = findProductIndexByLocalId(state.form.products, localId);

        if (productIndex === -1) {
            return;
        }

        const currentProduct = state.form.products[productIndex];
        const duplicatedProduct = getDefaultWidgetCarouselProduct({
            ...JSON.parse(JSON.stringify(currentProduct)),
            local_id: undefined,
            items: (currentProduct.items || []).map((item) =>
                getDefaultWidgetCarouselProductItem({
                    ...JSON.parse(JSON.stringify(item)),
                    local_id: undefined
                })
            )
        });

        state.form.products.splice(productIndex + 1, 0, duplicatedProduct);
    },

    REORDER_PRODUCTS(state, products) {
        state.form.products = products.map((product) =>
            getDefaultWidgetCarouselProduct(product)
        );
    },

    UPDATE_PRODUCT_FIELD(state, { localId, field, value }) {
        const productIndex = findProductIndexByLocalId(state.form.products, localId);

        if (productIndex === -1) {
            return;
        }

        Vue.set(state.form.products[productIndex], field, value);
    },

    UPDATE_PRODUCT_NESTED_FIELD(state, { localId, parentField, field, value }) {
        const productIndex = findProductIndexByLocalId(state.form.products, localId);

        if (productIndex === -1) {
            return;
        }

        if (!state.form.products[productIndex][parentField]) {
            Vue.set(state.form.products[productIndex], parentField, {});
        }

        Vue.set(state.form.products[productIndex][parentField], field, value);
    },

    SET_PRODUCT_IMAGE(state, { localId, image }) {
        const productIndex = findProductIndexByLocalId(state.form.products, localId);

        if (productIndex === -1) {
            return;
        }

        Vue.set(state.form.products[productIndex], "image", image);
    },

    ADD_PRODUCT_ITEM(state, { productLocalId, item }) {
        const productIndex = findProductIndexByLocalId(state.form.products, productLocalId);

        if (productIndex === -1) {
            return;
        }

        state.form.products[productIndex].items.push(
            getDefaultWidgetCarouselProductItem(item)
        );
    },

    REMOVE_PRODUCT_ITEM(state, { productLocalId, itemLocalId }) {
        const productIndex = findProductIndexByLocalId(state.form.products, productLocalId);

        if (productIndex === -1) {
            return;
        }

        const itemIndex = findProductItemIndexByLocalId(
            state.form.products[productIndex].items,
            itemLocalId
        );

        if (itemIndex === -1) {
            return;
        }

        state.form.products[productIndex].items.splice(itemIndex, 1);
    },

    DUPLICATE_PRODUCT_ITEM(state, { productLocalId, itemLocalId }) {
        const productIndex = findProductIndexByLocalId(state.form.products, productLocalId);

        if (productIndex === -1) {
            return;
        }

        const itemIndex = findProductItemIndexByLocalId(
            state.form.products[productIndex].items,
            itemLocalId
        );

        if (itemIndex === -1) {
            return;
        }

        const currentItem = state.form.products[productIndex].items[itemIndex];
        const duplicatedItem = getDefaultWidgetCarouselProductItem({
            ...JSON.parse(JSON.stringify(currentItem)),
            local_id: undefined
        });

        state.form.products[productIndex].items.splice(itemIndex + 1, 0, duplicatedItem);
    },

    UPDATE_PRODUCT_ITEM_FIELD(state, { productLocalId, itemLocalId, field, value }) {
        const productIndex = findProductIndexByLocalId(state.form.products, productLocalId);

        if (productIndex === -1) {
            return;
        }

        const itemIndex = findProductItemIndexByLocalId(
            state.form.products[productIndex].items,
            itemLocalId
        );

        if (itemIndex === -1) {
            return;
        }

        Vue.set(state.form.products[productIndex].items[itemIndex], field, value);
    },

    UPDATE_PRODUCT_ITEM_NESTED_FIELD(
        state,
        { productLocalId, itemLocalId, parentField, field, value }
    ) {
        const productIndex = findProductIndexByLocalId(state.form.products, productLocalId);

        if (productIndex === -1) {
            return;
        }

        const itemIndex = findProductItemIndexByLocalId(
            state.form.products[productIndex].items,
            itemLocalId
        );

        if (itemIndex === -1) {
            return;
        }

        if (!state.form.products[productIndex].items[itemIndex][parentField]) {
            Vue.set(state.form.products[productIndex].items[itemIndex], parentField, {});
        }

        Vue.set(state.form.products[productIndex].items[itemIndex][parentField], field, value);
    },

    UPDATE_PRODUCT_ITEM_I18N_FIELD(
        state,
        { productLocalId, itemLocalId, field, lang, value }
    ) {
        const productIndex = findProductIndexByLocalId(state.form.products, productLocalId);

        if (productIndex === -1) {
            return;
        }

        const itemIndex = findProductItemIndexByLocalId(
            state.form.products[productIndex].items,
            itemLocalId
        );

        if (itemIndex === -1) {
            return;
        }

        if (!state.form.products[productIndex].items[itemIndex][field]) {
            Vue.set(state.form.products[productIndex].items[itemIndex], field, {});
        }

        Vue.set(state.form.products[productIndex].items[itemIndex][field], lang, value);
    },

    REPLACE_PRODUCT_SOURCE(state, { localId, source }) {
        const productIndex = findProductIndexByLocalId(state.form.products, localId);

        if (productIndex === -1) {
            return;
        }

        const currentProduct = state.form.products[productIndex];

        const isActive =
            typeof source.is_active === "boolean"
                ? source.is_active
                : (typeof currentProduct.is_active === "boolean" ? currentProduct.is_active : true);

        const updatedProduct = {
            ...currentProduct,
            source_type: source.source_type || currentProduct.source_type || state.form.type || "benefits",
            product_id: source.product_id,
            product_name: source.product_name,
            product_name_en: source.product_name_en || "",
            price: source.price != null ? String(source.price) : "",
            discount_price: source.discount_price != null ? String(source.discount_price) : "",
            user_discount: source.user_discount != null ? String(source.user_discount) : "",
            discount: source.discount != null ? String(source.discount) : "",
            value: source.value != null ? String(source.value) : "",
            brand_name: source.brand_name || "",
            is_active: isActive,
            is_locked: !isActive,
            reason: source.reason || "",
            image: {
                file: null,
                preview: typeof source.image === "string" ? source.image : "",
                error: "",
                removed: false
            }
        };

        updatedProduct.items = reinterpolateProductItems(updatedProduct.items, updatedProduct);

        Vue.set(state.form.products, productIndex, updatedProduct);
    }
};

const actions = {
    initCreate({ commit }) {
        commit("SET_MODE", "create");
        commit("SET_FORM", normalizeWidgetCarouselForm({}));
        commit("SET_ACTIVE_TAB", "general");
        commit("SET_SELECTED_PRODUCT_ID", null);
    },

    initUpdate({ commit }, payload) {
        commit("SET_MODE", "update");
        commit("SET_FORM", normalizeWidgetCarouselForm(payload));
        commit("SET_ACTIVE_TAB", "general");
        commit("SET_SELECTED_PRODUCT_ID", null);
    },

    setActiveTab({ state, commit }, tab) {
        if (!state.form.is_active && tab === "products") {
            return;
        }

        commit("SET_ACTIVE_TAB", tab);
    },

    updateField({ state, commit }, payload) {
        if (
            payload.field === "type" &&
            Array.isArray(state.form.products) &&
            state.form.products.length > 0 &&
            state.form.type !== payload.value
        ) {
            return;
        }

        commit("SET_FIELD", payload);

        if (
            payload.field === "is_active" &&
            payload.value === false &&
            state.activeTab === "products"
        ) {
            commit("SET_ACTIVE_TAB", "general");
        }
    },

    updateI18nField({ commit }, payload) {
        commit("SET_I18N_FIELD", payload);
    },

    setGlobalBackgroundImage({ commit }, image) {
        commit("SET_GLOBAL_BACKGROUND_IMAGE", {
            file: image && image.file ? image.file : null,
            preview: image && image.preview ? image.preview : "",
            error: image && image.error ? image.error : "",
            removed: false
        });
    },

    clearGlobalBackgroundImage({ commit }) {
        commit("SET_GLOBAL_BACKGROUND_IMAGE", {
            file: null,
            preview: "",
            error: "",
            removed: true
        });
    },

    addProduct({ commit }, product) {
        commit("ADD_PRODUCT", product);
    },

    removeProduct({ commit }, localId) {
        commit("REMOVE_PRODUCT", localId);
    },

    duplicateProduct({ state, commit }, localId) {
        if (isLockedProduct(state.form.products, localId)) {
            return;
        }

        commit("DUPLICATE_PRODUCT", localId);
    },

    reorderProducts({ commit }, products) {
        commit("REORDER_PRODUCTS", products);
    },

    toggleProductSelection({ state, commit }, localId) {
        commit(
            "SET_SELECTED_PRODUCT_ID",
            state.selectedProductId === localId ? null : localId
        );
    },

    updateProductField({ state, commit }, payload) {
        if (isLockedProduct(state.form.products, payload.localId)) {
            return;
        }

        commit("UPDATE_PRODUCT_FIELD", payload);
    },

    updateProductNestedField({ state, commit }, payload) {
        if (isLockedProduct(state.form.products, payload.localId)) {
            return;
        }

        commit("UPDATE_PRODUCT_NESTED_FIELD", payload);
    },

    setProductImage({ state, commit }, payload) {
        if (isLockedProduct(state.form.products, payload.localId)) {
            return;
        }

        commit("SET_PRODUCT_IMAGE", {
            localId: payload.localId,
            image: {
                file: payload.image && payload.image.file ? payload.image.file : null,
                preview: payload.image && payload.image.preview ? payload.image.preview : "",
                error: payload.image && payload.image.error ? payload.image.error : "",
                removed: false
            }
        });
    },

    clearProductImage({ state, commit }, localId) {
        if (isLockedProduct(state.form.products, localId)) {
            return;
        }

        commit("SET_PRODUCT_IMAGE", {
            localId,
            image: {
                file: null,
                preview: "",
                error: "",
                removed: true
            }
        });
    },

    addProductItem({ state, commit }, payload) {
        if (isLockedProduct(state.form.products, payload.productLocalId)) {
            return;
        }

        commit("ADD_PRODUCT_ITEM", payload);
    },

    removeProductItem({ state, commit }, payload) {
        if (isLockedProduct(state.form.products, payload.productLocalId)) {
            return;
        }

        commit("REMOVE_PRODUCT_ITEM", payload);
    },

    duplicateProductItem({ state, commit }, payload) {
        if (isLockedProduct(state.form.products, payload.productLocalId)) {
            return;
        }

        commit("DUPLICATE_PRODUCT_ITEM", payload);
    },

    updateProductItemField({ state, commit }, payload) {
        if (isLockedProduct(state.form.products, payload.productLocalId)) {
            return;
        }

        commit("UPDATE_PRODUCT_ITEM_FIELD", payload);
    },

    updateProductItemNestedField({ state, commit }, payload) {
        if (isLockedProduct(state.form.products, payload.productLocalId)) {
            return;
        }

        commit("UPDATE_PRODUCT_ITEM_NESTED_FIELD", payload);
    },

    updateProductItemI18nField({ state, commit }, payload) {
        if (isLockedProduct(state.form.products, payload.productLocalId)) {
            return;
        }

        commit("UPDATE_PRODUCT_ITEM_I18N_FIELD", payload);
    },

    replaceProductSource({ commit }, payload) {
        commit("REPLACE_PRODUCT_SOURCE", payload);
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};